import { createFileRoute } from "@tanstack/react-router"
import { trpc } from "lib/trpc"
import { AlertCircle, Download, FileText, Minus, Plus } from "lucide-react"
import { useState } from "react"
import { useTranslation } from "react-i18next"
import ReactMarkdown from "react-markdown"
import remarkGfm from "remark-gfm"
import { toast } from "sonner"
import { ClientHeader } from "@/components/client-header"
import { ClientTabNavigation } from "@/components/client-tab-navigation"
import { DocumentList } from "@/components/documents/document-list"
import { UploadDocumentModal } from "@/components/modals/upload-document-modal"
import { Page } from "@/components/page"
import { RecommendationsSection } from "@/components/recommendations-section"
import { RefineReportsSection } from "@/components/refine-reports-section"
import { exportReportActionSummary } from "@/components/reports/exportReportActionSummary"
import { TrustRelationshipTree } from "@/components/trust-relationship-tree"
import { Button } from "@/components/ui/button"
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card"
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "@/components/ui/collapsible"
import { Label } from "@/components/ui/label"
import { SidebarTrigger } from "@/components/ui/sidebar"
import { Textarea } from "@/components/ui/textarea"
import { Typography } from "@/components/ui/typography"
import type { documentTypeEnum } from "../../../../../../../../api/db/schema/document"
import { ReportTypes } from "../../../../../../../../api/report-generation/report-types"
import type { RouterOutputs } from "../../../../../../../../api/routes/api/router"

type DocumentType = (typeof documentTypeEnum.enumValues)[number]

type TrustReport = RouterOutputs["reports"]["getOfType"][number]

type TrustSummaryItem = {
  title: string
  details?: string
  priority?: "HIGH" | "MEDIUM" | "LOW"
}

const trustSeverityRank = {
  HIGH: 0,
  MEDIUM: 1,
  LOW: 2,
} as const

export const Route = createFileRoute("/_authed/_professional/professional/reports/trust/$estateId")(
  {
    component: RouteComponent,
  },
)

function RouteComponent() {
  const { estateId } = Route.useParams()
  return <TrustReportRouteContent estateId={estateId} />
}

function TrustSummaryColumn({
  title,
  items,
  accent,
}: {
  title: string
  items: TrustSummaryItem[]
  accent: "red" | "blue"
}) {
  const [isExpanded, setIsExpanded] = useState(false)
  const isRed = accent === "red"

  const visibleItems = isExpanded ? items : items.slice(0, 3)
  const hiddenItemCount = Math.max(0, items.length - 3)

  return (
    <section>
      <h3
        className={
          isRed
            ? "mb-4 text-lg font-semibold text-red-600"
            : "mb-4 text-lg font-semibold text-blue-600"
        }
      >
        {title}
      </h3>

      {items.length > 0 ? (
        <>
          <ol className="space-y-3">
            {visibleItems.map((item, index) => (
              <li
                key={`${item.title}:${index}`}
                className="flex gap-4 rounded-lg border bg-background p-4"
              >
                <span
                  className={
                    isRed
                      ? "flex size-9 shrink-0 items-center justify-center rounded-full bg-red-100 text-sm font-semibold text-red-600"
                      : "flex size-9 shrink-0 items-center justify-center rounded-full bg-blue-100 text-sm font-semibold text-blue-600"
                  }
                  aria-hidden="true"
                >
                  {index + 1}
                </span>

                <div className="min-w-0 flex-1">
                  <div className="flex flex-col justify-between gap-2 sm:flex-row sm:items-start">
                    <p className="font-semibold leading-6 text-foreground">{item.title}</p>

                    {item.priority && (
                      <span
                        className={
                          isRed
                            ? "shrink-0 text-xs font-semibold text-red-600"
                            : "shrink-0 text-xs font-semibold text-blue-600"
                        }
                      >
                        {item.priority}
                      </span>
                    )}
                  </div>

                  {item.details && (
                    <p className="mt-1 text-sm leading-5 text-muted-foreground">{item.details}</p>
                  )}
                </div>
              </li>
            ))}
          </ol>

          {hiddenItemCount > 0 && (
            <Button
              type="button"
              variant="ghost"
              className="mt-3 w-full"
              onClick={() => setIsExpanded((current) => !current)}
            >
              {isExpanded ? (
                <>
                  <Minus className="mr-0.5 size-4" />
                  Show fewer
                </>
              ) : (
                <>
                  <Plus className="mr-0.5 size-4" />
                  Show {hiddenItemCount} more
                </>
              )}
            </Button>
          )}
        </>
      ) : (
        <div className="rounded-lg border border-dashed p-4">
          <p className="text-sm text-muted-foreground">No {title.toLowerCase()} are available.</p>
        </div>
      )}
    </section>
  )
}

function TrustSummaryCard({
  report,
  trustName,
  gaps,
  nextSteps,
  downloadButtonLabel,
  downloadButtonTitle,
  isDownloadDisabled,
  onDownload,
}: {
  report: TrustReport
  trustName: string
  gaps: TrustSummaryItem[]
  nextSteps: TrustSummaryItem[]
  downloadButtonLabel: string
  downloadButtonTitle?: string
  isDownloadDisabled: boolean
  onDownload: () => void
}) {
  const dateLabel = report.generationDate.toLocaleDateString("en-GB", {
    year: "numeric",
    month: "short",
    day: "2-digit",
  })

  return (
    <Card>
      <CardHeader className="border-b">
        <div className="flex flex-col justify-between gap-4 sm:flex-row sm:items-start">
          <div>
            <p className="text-sm font-semibold text-blue-700">Trust Review</p>

            <CardTitle className="mt-2 text-2xl">Top Gaps &amp; Next Steps</CardTitle>

            <p className="mt-2 text-sm text-muted-foreground">
              {trustName} • {dateLabel}
            </p>
          </div>

          <Button
            type="button"
            variant="outline"
            onClick={onDownload}
            disabled={isDownloadDisabled}
            title={downloadButtonTitle}
          >
            <Download className="mr-1 h-4 w-4" />
            {downloadButtonLabel}
          </Button>
        </div>
      </CardHeader>

      <CardContent className="pt-5">
        <div className="grid gap-6 lg:grid-cols-2">
          <TrustSummaryColumn title="Top gaps" items={gaps} accent="red" />

          <TrustSummaryColumn title="Next steps" items={nextSteps} accent="blue" />
        </div>

        <div className="mt-6 border-t pt-6">
          <Collapsible className="group">
            <div className="overflow-hidden rounded-lg border border-blue-200 bg-gradient-to-r from-blue-50 to-indigo-50">
              <CollapsibleTrigger asChild>
                <button
                  type="button"
                  className="flex w-full items-center justify-between p-5 text-left"
                >
                  <div className="flex items-center gap-4">
                    <FileText className="size-5 text-blue-600" />

                    <div>
                      <CardTitle className="text-lg font-semibold text-blue-800">
                        Trust Review Insights
                      </CardTitle>

                      <p className="text-sm text-muted-foreground">
                        Expand to read details about the report
                      </p>
                    </div>
                  </div>

                  <div className="text-blue-600">
                    <Plus className="size-5 group-data-[state=open]:hidden" />
                    <Minus className="size-5 group-data-[state=closed]:hidden" />
                  </div>
                </button>
              </CollapsibleTrigger>

              {report.summaryNotes && (
                <div className="px-5 pb-3">
                  <p className="line-clamp-2 text-sm font-medium text-gray-700">
                    {report.summaryNotes}
                  </p>
                </div>
              )}

              <CollapsibleContent>
                <div className="border-t border-blue-100 px-5 pb-5 pt-4">
                  {report.content ? (
                    <div className="prose max-w-none prose-slate text-sm">
                      <ReactMarkdown remarkPlugins={[remarkGfm]}>{report.content}</ReactMarkdown>
                    </div>
                  ) : (
                    <p className="text-sm text-muted-foreground">
                      No detailed report content is available.
                    </p>
                  )}
                </div>
              </CollapsibleContent>
            </div>
          </Collapsible>
        </div>
      </CardContent>
    </Card>
  )
}

export function TrustReportRouteContent({ estateId }: { estateId: string }) {
  const { t } = useTranslation()
  const [uploadModalOpen, setUploadModalOpen] = useState(false)
  const [additionalPrompt, setAdditionalPrompt] = useState("")
  const [isExporting, setIsExporting] = useState(false)

  const trustDocumentType: DocumentType = "TRUST"

  const { data: trustReports = [] } = trpc.reports.getOfType.useQuery({
    estateId,
    reportTypes: ["TRUST_REVIEW"],
  })

  const {
    data: trustInfographic,
    isLoading: isTrustInfographicLoading,
    isError: isTrustInfographicError,
  } = trpc.reports.getTrustInfographic.useQuery(
    { estateId },
    {
      enabled: trustReports.length > 0,
      retry: false,
    },
  )

  const queryUtils = trpc.useUtils()
  const gaps = [...(trustInfographic?.gapsIdentified ?? [])]
    .sort((a, b) => trustSeverityRank[a.severity] - trustSeverityRank[b.severity])
    .map((gap) => ({
      title: gap.issue,
      details: gap.details,
      priority: gap.severity,
    }))

  const nextSteps = [...(trustInfographic?.recommendations ?? [])]
    .sort((a, b) => a.order - b.order)
    .map((recommendation) => ({
      title: recommendation.action,
      details: recommendation.details,
      priority: recommendation.priority,
    }))

  const canDownload = gaps.length > 0 || nextSteps.length > 0
  const generateTrustReportMutation = trpc.reports.generate.useMutation({
    onSuccess: () => {
      toast.success(t("pages.reports.trust.generationStarted"), {
        description: t("pages.reports.estate.generateReports.emailNoticeReport"),
        duration: 8000,
      })
      queryUtils.reports.getOfType.invalidate({ estateId, reportTypes: ["TRUST_REVIEW"] })
      queryUtils.reports.getTrustInfographic.invalidate({ estateId })
    },
    onError: (error) => {
      toast.error(t("pages.reports.trust.generationError", { message: error.message }))
    },
  })

  async function handleDownload() {
    if (!canDownload) {
      toast.error("No trust gaps or next steps are available to export.")
      return
    }

    setIsExporting(true)

    try {
      const estate = await queryUtils.estates.getById.fetch({
        estateId,
      })

      await exportReportActionSummary({
        estateName: estate?.estateName ?? "Estate",
        reportTitle: "Trust Review",
        filePrefix: "Trust_Review",
        gaps: gaps.slice(0, 3),
        nextSteps: nextSteps.slice(0, 3),
      })

      toast.success("PowerPoint downloaded")
    } catch (error) {
      toast.error(error instanceof Error ? error.message : "Unable to generate the PowerPoint")
    } finally {
      setIsExporting(false)
    }
  }

  let downloadButtonLabel = "Download Summary"

  if (isExporting) {
    downloadButtonLabel = "Generating…"
  } else if (isTrustInfographicLoading) {
    downloadButtonLabel = "Loading…"
  }

  let downloadButtonTitle: string | undefined

  if (isTrustInfographicError) {
    downloadButtonTitle = "Trust infographic data could not be loaded"
  } else if (!canDownload) {
    downloadButtonTitle = "No trust gaps or next steps are available"
  }

  return (
    <Page
      showMobileNavigation={false}
      headerSlot={
        <ClientHeader
          estateId={estateId}
          mobileNavigationSlot={
            <SidebarTrigger className="size-10 shrink-0 md:hidden" aria-label="Open navigation" />
          }
        />
      }
      tabSlot={<ClientTabNavigation estateId={estateId} />}
    >
      <div className="space-y-6">
        <RecommendationsSection
          title="Trust Review Recommendations"
          headerAction={
            <Button
              type="button"
              variant="outline"
              onClick={handleDownload}
              disabled={
                isExporting || isTrustInfographicLoading || isTrustInfographicError || !canDownload
              }
              title={downloadButtonTitle}
            >
              <Download className="mr-1 h-4 w-4" />
              {downloadButtonLabel}
            </Button>
          }
          middleSection={<TrustRelationshipTree estateId={estateId} />}
          refineSection={
            <RefineReportsSection>
              <div className="space-y-4">
                <DocumentList
                  estateId={estateId}
                  documentType={trustDocumentType}
                  onDocumentDeleted={() => {
                    queryUtils.documents.listByType.invalidate()
                  }}
                />

                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  className="w-auto"
                  onClick={() => setUploadModalOpen(true)}
                >
                  <Plus className="h-4 w-4 mr-1" />
                  Add document
                </Button>

                <div className="space-y-2">
                  <Label htmlFor="additionalPrompt">
                    Prompt agent when generating or rerunning the report (optional)
                  </Label>
                  <Textarea
                    id="additionalPrompt"
                    placeholder="Add specific instructions for the AI analysis..."
                    value={additionalPrompt}
                    onChange={(e) => setAdditionalPrompt(e.target.value)}
                    className="min-h-[100px] bg-white"
                  />
                </div>

                <Button
                  type="button"
                  onClick={() => {
                    const prompt = additionalPrompt.trim() || undefined
                    toast.info(
                      prompt
                        ? `Sending report with prompt: "${prompt.slice(0, 50)}${prompt.length > 50 ? "..." : ""}"`
                        : "Sending report without additional prompt",
                    )
                    generateTrustReportMutation.mutate({
                      estateId,
                      reportType: [ReportTypes.TRUST_REVIEW],
                      additionalPrompt: prompt,
                    })
                  }}
                  className="w-full"
                  disabled={generateTrustReportMutation.isPending}
                >
                  {generateTrustReportMutation.isPending ? "Generating..." : "Run Report"}
                </Button>
                <Typography as="p" className="text-xs text-muted-foreground mt-3">
                  * Legacy Keeper reports use a complex agent orchestration to deliver personalised
                  insight for each client, saving hours of work. Due to the complex nature of the
                  report generation process you can expect each report to take between 10-20 minutes
                  as our agents evaluate the latest estate laws, user goals, and user data to
                  deliver a truly bespoke output for your clients and their families.
                </Typography>
              </div>
            </RefineReportsSection>
          }
        >
          {trustReports.length === 0 ? (
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center">
                  <AlertCircle className="mr-2 h-5 w-5 text-yellow-500" />
                  No Trust Reports Generated
                </CardTitle>
              </CardHeader>

              <CardContent>
                <Typography as="p" className="text-muted-foreground">
                  Generate a trust report to view insights and recommendations for this
                  estate&apos;s trust structures.
                </Typography>
              </CardContent>
            </Card>
          ) : (
            trustReports[0] && (
              <TrustSummaryCard
                report={trustReports[0]}
                trustName={trustInfographic?.header?.estateOwnerName ?? "Estate"}
                gaps={gaps}
                nextSteps={nextSteps}
                downloadButtonLabel={downloadButtonLabel}
                downloadButtonTitle={downloadButtonTitle}
                isDownloadDisabled={
                  isExporting ||
                  isTrustInfographicLoading ||
                  isTrustInfographicError ||
                  !canDownload
                }
                onDownload={handleDownload}
              />
            )
          )}
        </RecommendationsSection>
      </div>

      <UploadDocumentModal
        open={uploadModalOpen}
        setOpen={setUploadModalOpen}
        estateId={estateId}
        documentType={trustDocumentType}
        onSuccess={() => {
          queryUtils.documents.listByType.invalidate()
        }}
      />
    </Page>
  )
}
